<template>
    <div :class="$style.navbar">
        <div :class="$style.navbarBrand" class="navbar-brand">
            <div class="navbar-item">
                <router-link :class="$style.navbarLogo" :to="{ name: 'jbi.global' }">
                    <img src="@/jbi-shared/assets/images/jbi_navbar_img@2x.png" width="68.57" height="30" />
                </router-link>
                <router-link :class="$style.navbarLogo" :to="{ name: 'sumari.projects' }">
                    <div>
                        <img src="@/jbi-shared/assets/images/sumari_logo.svg" width="120" height="30" />
                    </div>
                </router-link>
            </div>
            <div class="navbar-item">
                <BaseNavDropdown
                    :userName="getUserName()"
                    :userEmail="accountDetail?.email || ''"
                    :navLinks="dropdownNavLinks"
                />
                <div :class="$style.helpMenu">
                    <b-dropdown position="is-bottom-left" aria-role="menu" :mobile-modal="false">
                        <template #trigger>
                            <a role="button" :class="$style.helpTrigger" title="Help Menu">
                                <span class="icon"><i class="fa fa-question"></i></span>
                            </a>
                        </template>
                        <b-dropdown-item has-link aria-role="menuitem">
                            <a href="https://jbi-global-wiki.refined.site/space/SKB" target="_blank" rel="noopener">
                                Help/FAQs <i class="fa fa-external-link" :class="$style.externalIcon"></i>
                            </a>
                        </b-dropdown-item>
                        <b-dropdown-item has-link aria-role="menuitem">
                            <a href="https://jbi-global-wiki.refined.site/space/SKB/3346270/Video+Tutorials" target="_blank" rel="noopener">
                                Tutorials <i class="fa fa-external-link" :class="$style.externalIcon"></i>
                            </a>
                        </b-dropdown-item>
                        <b-dropdown-item has-link aria-role="menuitem">
                            <a href="https://jbi-global-wiki.refined.site/space/MANUAL" target="_blank" rel="noopener">
                                JBI Manual For Evidence Synthesis <i class="fa fa-external-link" :class="$style.externalIcon"></i>
                            </a>
                        </b-dropdown-item>
                    </b-dropdown>
                </div>
                <a role="button" :class="$style.navBurger" @click="() => isMobileNavExpand = !isMobileNavExpand">
                    <span></span>
                    <span></span>
                    <span></span>
                </a>
            </div>
        </div>

        <!-- Horizontal navbar -->
        <div :class="$style.navItems">
            <template v-for="({ to, label, children }, i) in routes">
                <div
                    v-if="children"
                    :key="'desktop-children-' + i"
                    :class="[
                        $style.navItem,
                        $style.navDropdown,
                        isActiveRoute(to) ? $style.active : '',
                        isNavItemDisabled(to) ? $style.disabledNavItem : ''
                    ]"
                >
                    <span :class="$style.navDropdownTrigger">{{ label }} <span :class="$style.caret"></span></span>
                    <div v-if="!isNavItemDisabled(to)" :class="$style.navDropdownMenu">
                        <router-link
                            v-for="(child, j) in children"
                            :key="j"
                            :class="$style.navDropdownItem"
                            :to="getDestinationPath(`${child.to}`)"
                        >
                            {{ child.label }}
                        </router-link>
                    </div>
                </div>
                <span
                    v-else-if="isNavItemDisabled(to)"
                    :key="'desktop-disabled-' + i"
                    :class="[$style.navItem, $style.disabledNavItem]"
                >
                    {{ label }}
                </span>
                <router-link
                    v-else
                    :class="[$style.navItem, isActiveRoute(to) ? $style.active : '']"
                    :to="getDestinationPath(`${to}`)"
                    :key="'desktop-link-' + i"
                >
                    {{ label }}
                </router-link>
            </template>
        </div>

        <!-- Mobile view navbar -->
        <div :class="[$style.mobileNavItems, isMobileNavExpand ? $style.active : '']">
            <ul>
                <template v-for="({ to, label, children }, i) in routes">
                    <li
                        v-if="children"
                        :key="'mobile-children-' + i"
                        :class="[
                            $style.mobileNavItem,
                            isActiveRoute(to) ? $style.active : '',
                            isNavItemDisabled(to) ? $style.disabledNavItem : ''
                        ]"
                    >
                        <span>{{ label }}</span>
                        <ul v-if="!isNavItemDisabled(to)" :class="$style.mobileSubMenu">
                            <li v-for="(child, j) in children" :key="j">
                                <router-link
                                    :to="getDestinationPath(`${child.to}`)"
                                    custom
                                    v-slot="{ navigate }"
                                >
                                    <span @click="navigate">{{ child.label }}</span>
                                </router-link>
                            </li>
                        </ul>
                    </li>
                    <li
                        v-else-if="isNavItemDisabled(to)"
                        :key="'mobile-disabled-' + i"
                        :class="[$style.mobileNavItem, $style.disabledNavItem]"
                    >
                        <span>{{ label }}</span>
                    </li>
                    <li
                        v-else
                        :key="'mobile-link-' + i"
                        :class="[$style.mobileNavItem, isActiveRoute(to) ? $style.active : '']"
                    >
                        <router-link
                            :to="getDestinationPath(`${to}`)"
                            custom
                            v-slot="{ navigate }"
                        >
                            <span @click="navigate">{{ label }}</span>
                        </router-link>
                    </li>
                </template>
            </ul>
        </div>
    </div>
</template>
  
<script lang="ts">
import { NavBarLinks } from '@/jbi-shared/types/navbar.types';
import BaseNav from '@/jbi-shared/vue-components/BaseNav.vue';
import BaseNavDropdown from '@/jbi-shared/vue-components/BaseNavDropdown.vue';
import { navbarLinks } from '@/router/router.types';
import { AccountDetail } from '@/store/modules/account/account.types';
import { RootState } from '@/store/store';
import { Component, Mixins } from 'vue-property-decorator';
import { State } from 'vuex-class';
import ScreeningRoleMixin from '@/views/Screening/mixins/ScreeningRoleMixin';

const UPO_ALLOWED_NAV_PATHS = ['/overview', '/screening'];

@Component({
components: {
    BaseNav,
    BaseNavDropdown
}
})
export default class Navbar extends Mixins(ScreeningRoleMixin) {
    @State(
        (state: RootState) => state.account.getAccount.data
    )
    accountDetail!: AccountDetail;

    dropdownNavLinks = [
        {
            label: `Edit Profile`,
            onClick: () => window.location.href = `${process.env.VUE_APP_MYJBI_USER_PROFILE_URL}`,
        },
        {
            label: `Project Invitations`,
            onClick: () => window.location.href = `${process.env.VUE_APP_PROXY_CLIENT_URL}/#!/project-invitations`,
        },
        {
            label: `Change Password`,
            onClick: () => window.location.href = `${process.env.VUE_APP_MYJBI_USER_PASSWORD_URL}`,
        },
        {
            label: `Logout`,
            onClick: () => window.location.href = `${process.env.VUE_APP_MYJBI_LOGOUT_URL}`,
            class: `has-text-primary has-text-weight-semibold`,
        },
    ];
    
    routes: NavBarLinks[] = navbarLinks;

    isMobileNavExpand: boolean = false;

    getDestinationPath(to: string) {
        return '/projects/' + this.$route.params.projectId + to;
    }

    isActiveRoute(to?: string) {
        return this.$route.path.startsWith(this.getDestinationPath(`${to}`));
    }

    /** SUMDEV-3492: UPO may only use Overview + Screening from BCM nav. */
    isNavItemDisabled(to?: string): boolean {
        if (!this.isUnlicencedProjectOwner || !to) {
            return false;
        }
        return !UPO_ALLOWED_NAV_PATHS.some(
            (allowed) => to === allowed || to.startsWith(`${allowed}/`)
        );
    }

    getUserName() {
        if (this.accountDetail) {
            return `${this.accountDetail.firstName} ${this.accountDetail.lastName}`;
        }
        return ''
    }
}
</script>
  
<style lang="scss" module>
    $nav-border: 1px solid rgba(0, 0, 0, 0.2);
    $nav-shadow: 0 5px 5px -4px rgba(0, 0, 0, 0.2);

    .navbar {
        padding: 0 6vw;
        border-bottom: $nav-border;
        box-shadow: $nav-shadow;

        &#{&} {
            min-height: 60px;
        }
        .navbarBrand {
            justify-content: space-between;
        }
    .navbarLogo {
        padding-left: 10px;
    }
}

    .helpMenu {
        display: flex;
        align-items: center;
        margin-left: 8px;
    }

    .helpMenu :global(.dropdown .dropdown-trigger) {
        width: auto;
        display: flex;
    }

    .helpMenu :global(.dropdown .dropdown-menu) {
        left: auto;
        right: 0;
        max-width: 250px;
    }

    .helpMenu :global(.dropdown .dropdown-menu .dropdown-content .has-link a) {
        white-space: normal;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .externalIcon {
        font-size: 10px;
        color: #A5ADBA;
        margin-left: 12px;
        flex-shrink: 0;
    }

    .helpTrigger {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        color: #5E6B81;
        font-size: 16px;
        cursor: pointer;
        transition: background 0.15s ease, color 0.15s ease;

        &:hover, &:focus {
            background: rgba(0, 0, 0, 0.05);
            color: #1C2E4E;
        }

        &:active {
            transform: scale(0.95);
        }
    }

    .divider {
        border-left: 1px solid $grey-border-3;
        max-width: 300px;
        display: flex;
        height: 60%;
        margin-left: 16px;
        padding-left: 16px;
    }

    .navItems {
        width: 100%;
        min-height: 50px;
        display: flex;

        .navItem {
            flex: 2 1 auto;
            line-height: 46px;
            text-align: center;
            font-weight: 600;
            color: $font-grey;
            cursor: pointer;
            &.active {
                box-shadow: 0px -10px 15px #fff inset, 0px 0px 5px rgba(0, 0, 0, 0.2) inset, 0px 10px 0px #fff;
                border-width: 1px 1px 0px 1px;
                border-color: $primary-border-color;
                border-style: solid;
                border-radius: 5px 5px 0 0;
                color: $primary;
            }
            &.disabledNavItem {
                color: #b0b0b0;
                cursor: not-allowed;
                pointer-events: none;
            }
        }
    }

    .navDropdown {
        position: relative;
        cursor: pointer;

        .navDropdownTrigger {
            display: block;
            line-height: 46px;
            text-align: center;
        }

        .caret {
            display: inline-block;
            width: 0;
            height: 0;
            margin-left: 4px;
            vertical-align: middle;
            border-top: 4px solid;
            border-right: 4px solid transparent;
            border-left: 4px solid transparent;
        }

        .navDropdownMenu {
            display: none;
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            background: #fff;
            border: 1px solid rgba(0, 0, 0, 0.15);
            border-radius: 4px;
            box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
            min-width: 200px;
            z-index: 1000;
            padding: 5px 0;

            &::before {
                content: '';
                position: absolute;
                bottom: 100%;
                left: 0;
                right: 0;
                height: 16px;
            }
        }

        &:hover .navDropdownMenu {
            display: block;
        }

        .navDropdownItem {
            display: block;
            padding: 8px 20px;
            color: #333;
            white-space: nowrap;
            text-align: left;
            font-weight: 400;
            font-size: 14px;
            line-height: 1.42857143;

            &:hover {
                background: #f5f5f5;
                color: #262626;
                text-decoration: none;
            }
        }
    }

    .mobileSubMenu {
        padding: 4px 0;
        list-style: none;

        li {
            padding: 4px 0;
            font-size: 0.9em;
            color: #666;
            cursor: pointer;
        }
    }

    .navBurger {
        display: none;
        width: 54px;
        border: 0.5px solid #C5C5C5;
        border-radius: 3px;
        padding: 5px 0;
        margin-left: 5vw;
        
        span {
            display: block;
            width: 33px;
            height: 3.5px;
            margin: 5px auto;
            border-radius: 2px;
            position: relative;
            background: $grey-border-3;
        }
    }

    .mobileNavItems {
        display: none;
        width: 100vw;
        margin-left: -6vw;
        background: #FFFFFF;
        height: 0;
        overflow-y: hidden;
        transition: height 1s;

        &.active {
            height: 38vh;
            border-bottom: $nav-border;
            box-shadow: $nav-shadow;
        }

        ul {
            padding: 20px 0;

            .mobileNavItem {
                color: $font-grey;
                text-align: center;
                padding: 4px 0;
                margin: 5px 0;
                cursor: pointer;

                &.active {
                    color: $primary;
                    border-top: 1px solid #E0E0E0;
                    border-bottom: 1px solid #E0E0E0;
                    box-shadow: 0px 0px 5px #E0E0E0 inset;
                }

                &.disabledNavItem {
                    color: #b0b0b0;
                    cursor: not-allowed;
                    pointer-events: none;
                }
            }
        }
    }

    @media screen and (max-width: 992px)  {
        .navbar {
            padding: 1vh 6vw;
            position: sticky;
            top: 0;
            z-index: 50;
            background: #FFFFFF;
            max-height: 8.5vh;
        }

        .navBurger {
            display: block;
        }

        .navItems {
            display: none;
        }

        .mobileNavItems {
            display: block;
        }

    }
</style>
